import React from 'react';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import { store } from './app/store';
import App from './App';
import reportWebVitals from './reportWebVitals';
import './index.css';
import buildInfo from './build-info.json';

// Identidad del programa (dinámica por dominio)
try {
  const isHanna = window.location.hostname.includes('hanna');
  const PROGRAM_BRAND = isHanna ? 'HANNA | Tienda Online' : 'Ventio';
  
  // Establecer titulo
  document.title = PROGRAM_BRAND;

  // Establecer favicon
  let link = document.querySelector("link[rel='icon']");
  if (!link) {
    link = document.createElement('link');
    link.setAttribute('rel', 'icon');
    document.head.appendChild(link);
  }
  
  if (isHanna) {
    link.setAttribute('href', '/favicon.png');
  } else {
    link.setAttribute('href', process.env.PUBLIC_URL + '/branding/ventio-favicon.svg');
  }
} catch { }

// Exponer commit/build en runtime para diagnóstico de despliegue
try {
  if (buildInfo && buildInfo.commit) {
    try { window.__APP_COMMIT__ = buildInfo.commit; } catch (e) { }
    try { window.__APP_BUILD_TIME__ = buildInfo.buildTime || null; } catch (e) { }
    try { console.info('App build commit:', buildInfo.commit); } catch (e) { }
  }
} catch (e) { }

// Forzar modo escritorio por defecto (desactiva comportamientos móviles y bypass del guard)
try {
  const stored = localStorage.getItem('forceDesktop');
  const force = stored === null ? '1' : stored; // default ON
  if (force === '1') {
    document.documentElement.classList.add('desktop-only');
    // Evitar bloqueos de MobileGuard si se detecta viewport chico
    localStorage.setItem('mobileGuardBypass', '1');
  }
  if (stored === null) localStorage.setItem('forceDesktop', force);
} catch { }

// Dev: listen for impersonation session posted from backend accept handshake
// (dev-only listener removed before commit)
// On app start, detect a fragment like #impersonation=<base64> and bootstrap session
// This supports the accept page redirect which embeds the session JSON in the fragment.
try {
  (function () {
    try {
      const h = window.location.hash || '';
      if (!h) return;
      const m = h.match(/[#&]impersonation=([^&]+)/);
      if (!m) return;
      const enc = decodeURIComponent(m[1]);
      try {
        const json = JSON.parse(decodeURIComponent(escape(atob(enc))));
        if (json) {
          try { localStorage.setItem('user', JSON.stringify(json)); } catch (_) { }
          // Remove fragment and reload to let SPA pick up the user from localStorage
          try { window.history.replaceState(null, '', window.location.pathname + window.location.search); } catch (_) { }
          try { window.location.replace('/'); } catch (_) { window.location.reload(); }
        }
      } catch (e) { /* ignore parse errors */ }
    } catch (_) { }
  })();
} catch (_) { }

// Session bootstrap when an HttpOnly cookie exists but localStorage.user is not populated.
// This happens with the admin impersonation flow: backend sets an HttpOnly `accessToken` cookie
// and redirects to the tenant frontend. The cookie cannot be read from JS, so we call
// GET /api/auth/session with credentials included; the backend will return the session JSON
// when the cookie is present. If we receive a user, persist it to localStorage so the SPA
// initializes correctly (PrivateRoute / Redux auth slice rely on localStorage 'user').
try {
  (async function bootstrapSessionFromCookie() {
    try {
      if (typeof window === 'undefined' || !window.localStorage) return;
      // Permitir desactivar totalmente el bootstrap en dev para evitar bucles
      if (window.__DISABLE_SESSION_BOOTSTRAP__ === true) return;

      const existing = window.localStorage.getItem('user');
      const path = window.location.pathname || '/';
      const isStatic = path.startsWith('/website') || path.startsWith('/print') || path.startsWith('/admin-console');
      const isLogin = path === '/login';
      const isLocalhost = (window.location.hostname || '').toLowerCase() === 'localhost';

      // En localhost, evitar recargas/redirects agresivos: sólo poblar user si la cookie existe
      if (!existing && !isStatic) {
        try {
          const resp = await fetch('/api/auth/session', { method: 'GET', credentials: 'include' });
          if (resp && resp.status === 200) {
            const json = await resp.json();
            if (json && json._id) {
              try { localStorage.setItem('user', JSON.stringify(json)); } catch (_) { }
              // En dev: no forzar reload; dejar que la app inicialice con el user
              if (!isLocalhost) {
                try { window.location.replace(window.location.pathname + window.location.search); } catch (_) { window.location.reload(); }
              }
            }
          }
        } catch (_) { /* ignore network errors */ }
      } else if (existing) {
        // Revalidación de sesión:
        // - En localhost: saltar revalidación para evitar bucles por cookies ausentes
        // - En producción: validar y redirigir a /login si la cookie expiró
        if (!isLocalhost) {
          try {
            const resp = await fetch('/api/auth/session', { method: 'GET', credentials: 'include' });
            if (!resp || resp.status !== 200) {
              try { localStorage.removeItem('user'); } catch (_) { }
              if (!isLogin) {
                try { window.location.href = '/login'; } catch (_) { }
              }
            }
          } catch (_) {
            try { localStorage.removeItem('user'); } catch (_) { }
            if (!isLogin) {
              try { window.location.href = '/login'; } catch (_) { }
            }
          }
        }
      }
    } catch (_) { /* swallow */ }
  })();
} catch (_) { }

const container = document.getElementById('root');
const root = createRoot(container);

root.render(
  <React.StrictMode>
    <Provider store={store}>
      <App />
    </Provider>
  </React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();

// En desarrollo, desregistrar cualquier Service Worker activo para evitar problemas de caché agresiva
if (process.env.NODE_ENV !== 'production' && 'serviceWorker' in navigator) {
  navigator.serviceWorker.ready.then((registration) => {
    registration.unregister();
  }).catch((error) => {
    console.error(error.message);
  });

  navigator.serviceWorker.getRegistrations().then((registrations) => {
    for (let registration of registrations) {
      registration.unregister().then((bool) => {
        if (bool) console.log('SW Desregistrado en desarrollo');
      });
    }
  });
}

// Registrar Service Worker para modo offline (solo en producción)
if ('serviceWorker' in navigator && process.env.NODE_ENV === 'production') {
  window.addEventListener('load', () => {
    navigator.serviceWorker
      .register('/service-worker.js')
      .then((registration) => {
        console.log('[SW] Service Worker registrado exitosamente:', registration.scope);

        // Verificar actualizaciones cada 5 minutos
        setInterval(() => {
          registration.update();
        }, 5 * 60 * 1000);

        // Escuchar cambios en el Service Worker
        registration.addEventListener('updatefound', () => {
          const newWorker = registration.installing;

          newWorker.addEventListener('statechange', () => {
            if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
              // Hay una nueva versión disponible
              console.log('[SW] Nueva versión disponible');

              // Opcional: Mostrar notificación al usuario
              if (window.confirm('Hay una nueva versión disponible. ¿Deseas actualizar?')) {
                newWorker.postMessage({ type: 'SKIP_WAITING' });
                window.location.reload();
              }
            }
          });
        });
      })
      .catch((error) => {
        console.error('[SW] Error al registrar Service Worker:', error);
      });

    // Recargar cuando el Service Worker tome control
    let refreshing = false;
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      if (!refreshing) {
        refreshing = true;
        window.location.reload();
      }
    });
  });
}
